import { api } from "@/lib/apiClient";

export type TrialBalanceLine = {
  account_id: number;
  account_code: string;
  account_name: string;
  account_class: string;
  account_group: string | null;
  debit: number;
  credit: number;
};

export type TrialBalance = {
  filters: {
    as_at: string | null;
    hide_zero: boolean;
  };
  lines: TrialBalanceLine[];
  totals: {
    debit: number;
    credit: number;
    balanced: boolean;
  };
};

type ApiTrialBalance = {
  filters?: {
    as_at?: string | null;
    hide_zero?: boolean;
  };
  lines?: Array<Record<string, unknown>>;
  totals?: {
    debit?: number;
    credit?: number;
    balanced?: boolean;
  };
};

export type FetchTrialBalanceParams = {
  asAt?: string | null;
  hideZero?: boolean;
};

function queryParams(params: FetchTrialBalanceParams = {}) {
  return {
    ...(params.asAt ? { as_at: params.asAt } : {}),
    ...(params.hideZero ? { hide_zero: 1 } : {}),
  };
}

async function downloadBlob(url: string, params: FetchTrialBalanceParams, filename: string): Promise<void> {
  const response = await api.get(url, { params: queryParams(params), responseType: "blob" });
  const blob = new Blob([response.data], {
    type: response.headers["content-type"] || "application/octet-stream",
  });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

function mapLine(row: Record<string, unknown>): TrialBalanceLine {
  return {
    account_id: Number(row.account_id ?? 0),
    account_code: String(row.account_code ?? ""),
    account_name: String(row.account_name ?? ""),
    account_class: String(row.account_class ?? ""),
    account_group: (row.account_group as string | null) ?? null,
    debit: Number(row.debit ?? 0),
    credit: Number(row.credit ?? 0),
  };
}

export async function fetchTrialBalance(params: FetchTrialBalanceParams = {}): Promise<TrialBalance> {
  const response = await api.get<{ data: ApiTrialBalance }>("/api/v1/accounts/trial-balance", {
    params: queryParams(params),
  });
  const data = response.data?.data ?? {};
  return {
    filters: {
      as_at: data.filters?.as_at ?? null,
      hide_zero: Boolean(data.filters?.hide_zero),
    },
    lines: Array.isArray(data.lines) ? data.lines.map(mapLine) : [],
    totals: {
      debit: Number(data.totals?.debit ?? 0),
      credit: Number(data.totals?.credit ?? 0),
      balanced: Boolean(data.totals?.balanced),
    },
  };
}

export async function downloadTrialBalancePdf(params: FetchTrialBalanceParams = {}): Promise<void> {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  await downloadBlob("/api/v1/accounts/trial-balance/pdf", params, `trial-balance-${stamp}.pdf`);
}

export async function downloadTrialBalanceExcel(params: FetchTrialBalanceParams = {}): Promise<void> {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  await downloadBlob("/api/v1/accounts/trial-balance/excel", params, `trial-balance-${stamp}.csv`);
}
